import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  rectIntersection, closestCenter,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LinkIcon from '@mui/icons-material/Link';
import BlockIcon from '@mui/icons-material/Block';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import CardCallsTab from '@/components/admin/dialpad/CardCallsTab';
import useAlert from '@/components/feedback/useAlert';

import KanbanColumn from '@/components/workflow/KanbanColumn';
import AppBadge from '@/components/workflow/AppBadge';
import CommentThread from '@/components/workflow/CommentThread';
import StatusHistoryLog from '@/components/workflow/StatusHistoryLog';

import {
  getBoard, getCards, getCard, createCard, updateCard, moveCard,
  deactivateCard, linkChemist, sendTrainingEmail,
  addComment, updateAppStatus, syncAppStatus, checkDuplicate, getChemists,
  archiveCard, unarchiveCard, getArchivedCards,
} from '@/services/workflowService';

const BOARD_ID = 1;
const ALL_APPS = ['Quda','HaloPay','HaloText','DocLink','MediDrop','Zephyr','Webbit','HaloHire','HaloExpire'];

// Column keys that are "dead end" — chemist linking is hidden here
const DEAD_END_COL_KEYS = new Set(['not_proceeding']);
const LIVE_KEY = 'live';

const LeadCard = ({ card, onClick, onChemistClick, isSelected, onToggleSelect, onArchive }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(card.id),
  });

  const hasGroupColor = !!card.group_color;
  const cardStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    // suppress group colour while selected so the selection ring reads clearly
    ...(hasGroupColor && !isSelected ? { backgroundColor: card.group_color } : {}),
  };

  return (
    <div
      ref={setNodeRef} style={cardStyle} {...attributes} {...listeners}
      onClick={() => onClick(card)}
      className={`rounded-lg shadow-sm border p-3 cursor-pointer transition-all duration-150 group
        ${isSelected
          ? 'bg-sky-50 border-sky-400 ring-2 ring-sky-300 shadow-md'
          : hasGroupColor
            ? 'border-black/10 hover:border-black/20 hover:shadow-md'
            : 'bg-white border-gray-100 hover:shadow-md hover:border-sky-200'}`}
    >
      <div className="flex items-start justify-between gap-2">
        {/* Checkbox — stopPropagation on both pointer and click so DnD + drawer don't fire */}
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {}}
            onClick={(e) => { e.stopPropagation(); onToggleSelect(card.id); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 rounded accent-sky-500 cursor-pointer"
          />
          <p className="text-sm font-semibold text-gray-800 leading-tight group-hover:text-sky-700 transition-colors">
            {card.pharmacy_name}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <AppBadge app={card.primary_app_focus} size="xs" />
          {card.chemist_uuid && (
            <button
              onClick={(e) => { e.stopPropagation(); onChemistClick(card.chemist_uuid); }}
              className="p-0.5 rounded text-gray-400 hover:text-sky-500 hover:bg-sky-50 transition"
            >
              <PersonOutlinedIcon style={{ fontSize: 14 }} />
            </button>
          )}
          {onArchive && (
            <button
              title="Archive this lead"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onArchive(card.id); }}
              className="p-0.5 rounded text-gray-300 hover:text-emerald-600 hover:bg-emerald-50 transition opacity-0 group-hover:opacity-100"
            >
              <ArchiveOutlinedIcon style={{ fontSize: 14 }} />
            </button>
          )}
        </div>
      </div>
      {card.contact_name && <p className="text-xs text-gray-500 mt-1 ml-5">{card.contact_name}</p>}
      {card.pharmacy_phone && <p className="text-xs text-gray-400 mt-0.5 ml-5">{card.pharmacy_phone}</p>}
      {card.parent_group_name && (
        <div className="flex items-center gap-1 mt-1.5 ml-5">
          <span className="text-[10px] font-semibold text-gray-600 bg-black/10 rounded px-1.5 py-0.5 leading-tight">
            {card.parent_group_name}
          </span>
        </div>
      )}
      {card.training_email_sent === 1 && (
        <div className="flex items-center gap-1 mt-1 ml-5 text-[10px] text-emerald-700 font-medium">
          <EmailOutlinedIcon style={{ fontSize: 11 }} /> Training email sent
        </div>
      )}
    </div>
  );
};

const NewLeadModal = ({ onClose, onCreated, boardColumns }) => {
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const { showAlert, AlertSnackbar } = useAlert();

  // Selectable columns — exclude dead-end columns (not_proceeding)
  const selectableColumns = useMemo(
    () => (boardColumns || []).filter(c => !DEAD_END_COL_KEYS.has(c.key)),
    [boardColumns]
  );
  const defaultColumnId = selectableColumns[0]?.id ?? null;
  const [selectedColumnId, setSelectedColumnId] = useState(defaultColumnId);

  // Keep default in sync if boardColumns loads after mount
  useEffect(() => {
    if (selectedColumnId == null && defaultColumnId != null) {
      setSelectedColumnId(defaultColumnId);
    }
  }, [defaultColumnId]); // eslint-disable-line react-hooks/exhaustive-deps

  // New lead form state
  const [form, setForm] = useState({
    pharmacy_name: '', pharmacy_phone: '', contact_name: '',
    pharmacy_email: '', primary_app_focus: 'Quda',
  });
  const [dupes, setDupes] = useState(null);
  const [checking, setChecking] = useState(false);

  // Existing chemist state
  const [chemists, setChemists] = useState([]);
  const [chemistsLoading, setChemistsLoading] = useState(false);
  const [selectedChemist, setSelectedChemist] = useState(null);
  const [chemistSearch, setChemistSearch] = useState('');
  const [existingAppFocus, setExistingAppFocus] = useState('Quda');

  const [saving, setSaving] = useState(false);
  const dupeTimer = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Load unlinked chemists when switching to existing mode
  useEffect(() => {
    if (mode !== 'existing') return;
    setChemistsLoading(true);
    getChemists(true)
      .then(c => setChemists(c || []))
      .catch(console.error)
      .finally(() => setChemistsLoading(false));
  }, [mode]);

  const filteredChemists = chemistSearch.trim()
    ? chemists.filter(c => c.chemistName.toLowerCase().includes(chemistSearch.toLowerCase()))
    : chemists;

  const handleSelectChemist = (chemist) => {
    setSelectedChemist(chemist);
  };

  const triggerDupeCheck = (name, phone) => {
    clearTimeout(dupeTimer.current);
    if (!name && !phone) { setDupes(null); return; }
    dupeTimer.current = setTimeout(async () => {
      setChecking(true);
      try {
        const result = await checkDuplicate({ pharmacy_name: name || undefined, pharmacy_phone: phone || undefined });
        setDupes(result);
      } catch {}
      setChecking(false);
    }, 600);
  };

  const hasDupes = dupes && (dupes.cards?.length > 0 || dupes.chemists?.length > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mode === 'existing') {
        if (!selectedChemist) return;
        await createCard({
          board_id:          BOARD_ID,
          column_id:         selectedColumnId,
          chemist_id:        selectedChemist.chemistID,
          pharmacy_name:     selectedChemist.chemistName,
          pharmacy_phone:    selectedChemist.phone  || '',
          pharmacy_email:    selectedChemist.email  || '',
          primary_app_focus: existingAppFocus,
        });
      } else {
        await createCard({ board_id: BOARD_ID, column_id: selectedColumnId, ...form });
      }
      onCreated();
      onClose();
    } catch (err) {
      // phappsRequest throws on non-2xx — a 409 means the chemist is already in the pipeline
      if (err.status === 409) {
        showAlert({
          severity: 'warning',
          title:    'Already in Pipeline',
          message:  `${selectedChemist?.chemistName || 'This chemist'} already has an active lead in the onboarding pipeline.`,
        });
      } else {
        console.error(err);
        showAlert({ severity: 'error', message: err.message || 'Failed to create lead. Please try again.' });
      }
    }
    setSaving(false);
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#002169]">New Lead</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition"><CloseIcon /></button>
        </div>

        {/* Mode toggle */}
        <div className="flex border-b border-gray-100">
          {[
            { key: 'new',      label: 'New Lead',         desc: 'Not in our system yet' },
            { key: 'existing', label: 'Existing Chemist', desc: 'Already has an account' },
          ].map(m => (
            <button key={m.key} type="button" onClick={() => setMode(m.key)}
              className={`flex-1 px-4 py-3 text-left transition-all
                ${mode === m.key ? 'bg-sky-50 border-b-2 border-sky-500' : 'hover:bg-gray-50'}`}>
              <p className={`text-xs font-bold ${mode === m.key ? 'text-sky-600' : 'text-gray-600'}`}>{m.label}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{m.desc}</p>
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3">
          {mode === 'new' ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pharmacy Name *</label>
                  <input
                    required value={form.pharmacy_name}
                    onChange={(e) => { set('pharmacy_name', e.target.value); triggerDupeCheck(e.target.value, form.pharmacy_phone); }}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</label>
                  <input
                    value={form.pharmacy_phone}
                    onChange={(e) => { set('pharmacy_phone', e.target.value); triggerDupeCheck(form.pharmacy_name, e.target.value); }}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact Name</label>
                  <input
                    value={form.contact_name}
                    onChange={(e) => set('contact_name', e.target.value)}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</label>
                  <input
                    type="email" value={form.pharmacy_email}
                    onChange={(e) => set('pharmacy_email', e.target.value)}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Primary App Focus *</label>
                  <select
                    required value={form.primary_app_focus}
                    onChange={(e) => set('primary_app_focus', e.target.value)}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                  >
                    {ALL_APPS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              {checking && <p className="text-xs text-gray-400">Checking for duplicates…</p>}
              {hasDupes && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs">
                  <p className="font-semibold text-amber-700 mb-1">Possible duplicates found:</p>
                  {dupes.cards?.map(c => (
                    <p key={c.id} className="text-amber-600">Lead: {c.pharmacy_name} ({c.column_label})</p>
                  ))}
                  {dupes.chemists?.map(c => (
                    <p key={c.chemistID} className="text-amber-600">Chemist: {c.chemistName}</p>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Search Chemist *</label>
                <input
                  value={chemistSearch}
                  onChange={(e) => { setChemistSearch(e.target.value); setSelectedChemist(null); }}
                  placeholder="Type pharmacy name to search…"
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                />
              </div>

              {chemistsLoading ? (
                <p className="text-xs text-gray-400 text-center py-2">Loading chemists…</p>
              ) : chemistSearch.trim() && filteredChemists.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No unlinked chemists match "{chemistSearch}".</p>
              ) : chemistSearch.trim() ? (
                <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {filteredChemists.slice(0, 25).map(c => (
                    <button key={c.chemistID} type="button"
                      onClick={() => handleSelectChemist(c)}
                      className={`w-full text-left px-3 py-2 text-sm transition
                        ${selectedChemist?.chemistID === c.chemistID
                          ? 'bg-sky-50 text-sky-700 font-semibold'
                          : 'hover:bg-gray-50 text-gray-700'}`}>
                      <p className="font-medium">{c.chemistName}</p>
                      {c.phone && <p className="text-xs text-gray-400">{c.phone}</p>}
                    </button>
                  ))}
                </div>
              ) : null}

              {selectedChemist && (
                <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-700">
                  <p className="font-semibold">Selected: {selectedChemist.chemistName}</p>
                  {selectedChemist.phone  && <p>{selectedChemist.phone}</p>}
                  {selectedChemist.email  && <p>{selectedChemist.email}</p>}
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Primary App Focus *</label>
                <select
                  value={existingAppFocus}
                  onChange={(e) => setExistingAppFocus(e.target.value)}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
                >
                  {ALL_APPS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </>
          )}

          {/* ── Column selector (new lead mode only) ── */}
          {mode === 'new' && selectableColumns.length > 1 && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Start in Column</label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {selectableColumns.map(col => {
                  const isActive = selectedColumnId === col.id;
                  return (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() => setSelectedColumnId(col.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all
                        ${isActive
                          ? 'border-transparent text-white shadow-sm'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`}
                      style={isActive ? { backgroundColor: col.color || '#0ea5e9' } : {}}
                    >
                      {col.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || (mode === 'existing' && !selectedChemist)}
              className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-600 disabled:opacity-50 transition"
            >
              {saving ? 'Creating…' : mode === 'new' && hasDupes ? 'Create Anyway' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
    {AlertSnackbar}
    </>
  );
};

const EditableField = ({ label, value, type = 'text', onSave, required = false }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const [error, setError] = useState('');
  useEffect(() => setVal(value), [value]);

  const commit = () => {
    if (required && !val.trim()) {
      setError(`${label} is required.`);
      return;
    }
    setError('');
    onSave(val);
    setEditing(false);
  };

  return (
    <div>
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {editing ? (
        <div className="flex flex-col gap-1 mt-1">
          <div className="flex gap-2">
            <input
              type={type} value={val} autoFocus
              onChange={(e) => { setVal(e.target.value); if (error) setError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setError(''); } }}
              className={`flex-1 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300 ${error ? 'border-red-400' : 'border-sky-300'}`}
            />
            <button onClick={commit} className="px-2 py-1.5 bg-sky-500 text-white rounded-lg text-xs font-semibold">Save</button>
            <button onClick={() => { setEditing(false); setError(''); }} className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs">Cancel</button>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      ) : (
        <div
          onClick={() => setEditing(true)}
          className="mt-1 px-3 py-2 rounded-lg border border-transparent hover:border-sky-200 hover:bg-sky-50 text-sm text-gray-800 cursor-text transition min-h-[36px]"
        >
          {val || <span className="text-gray-400 italic">Click to edit…</span>}
        </div>
      )}
    </div>
  );
};

const LeadDetailDrawer = ({ cardId, onClose, onUpdate, boardColumns, liveColId, onArchive }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [chemists, setChemists] = useState([]);
  const [activeTab, setActiveTab] = useState('details');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const card = await getCard(cardId);
      setData(card);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [cardId]);

  useEffect(() => { reload(); }, [reload]);

  // Chemists list only needs to load once on mount — not on every card reload
  useEffect(() => {
    getChemists(true).then(c => setChemists(c || [])).catch(console.error);
  }, []);

  const handleFieldSave = async (field, value) => {
    setSaving(true);
    try {
      await updateCard(cardId, { [field]: value });
      setData(d => ({ ...d, [field]: value }));
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleColumnChange = async (columnId) => {
    setSaving(true);
    try {
      await moveCard(cardId, Number(columnId));
      await reload();
      onUpdate && onUpdate();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleSendEmail = async () => {
    setEmailSending(true);
    try {
      await sendTrainingEmail(cardId);
      setData(d => ({ ...d, training_email_sent: 1 }));
    } catch (e) { console.error(e); }
    setEmailSending(false);
  };

  const handleLinkChemist = async (chemist_id) => {
    setSaving(true);
    try { await linkChemist(cardId, Number(chemist_id)); await reload(); } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Mark this lead as Not Proceeding?')) return;
    if (!notProceedingColId) return;
    await moveCard(cardId, notProceedingColId);
    onClose();
    onUpdate && onUpdate();
  };

  const handleAddComment = async (comment) => {
    await addComment(cardId, comment);
    await reload();
  };

  const handleSyncApps = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      await syncAppStatus(cardId);
      await reload();
      setSyncMsg('Synced');
      setTimeout(() => setSyncMsg(''), 3000);
    } catch (e) {
      console.error(e);
      setSyncMsg('Sync failed');
      setTimeout(() => setSyncMsg(''), 3000);
    }
    setSyncing(false);
  };

  const handleAppToggle = async (appName, field, value) => {
    const updated = (data.apps || []).map(a =>
      a.app_name === appName ? { ...a, [field]: value } : a
    );
    setData(d => ({ ...d, apps: updated }));
    await updateAppStatus(cardId, updated.map(a => ({
      app_name: a.app_name,
      is_interested: !!a.is_interested,
      is_installed:  !!a.is_installed,
      is_trained:    !!a.is_trained,
    })));
  };

  const TABS = ['details','apps','calls','comments','history'];
  // Computed from loaded columns — works regardless of column IDs in the DB
  const notProceedingColId = boardColumns.find(c => c.key === 'not_proceeding')?.id ?? null;
  const chemistLinkColIds  = new Set(
    boardColumns.filter(c => !DEAD_END_COL_KEYS.has(c.key)).map(c => c.id)
  );
  const showChemistLink = data && chemistLinkColIds.has(data.column_id);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col h-full">
        <div
          className="flex items-center justify-between px-6 py-4 text-white flex-shrink-0 transition-colors"
          style={{ backgroundColor: data?.group_color ? data.group_color : '#002169' }}
        >
          <div>
            <h2 className={`text-lg font-bold ${data?.group_color ? 'text-gray-900' : 'text-white'}`}>
              {data?.pharmacy_name || 'Loading…'}
            </h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {data?.primary_app_focus && <AppBadge app={data.primary_app_focus} />}
              {data?.parent_group_name && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/15 text-gray-800 uppercase tracking-wide">
                  {data.parent_group_name}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data?.chemist_uuid && (
              <button
                onClick={() => navigate(`/admin-functions/chemist/${data.chemist_uuid}`)}
                className={`p-1.5 rounded-lg transition ${data?.group_color ? 'text-gray-600 hover:text-gray-900 hover:bg-black/10' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
              >
                <PersonOutlinedIcon style={{ fontSize: 20 }} />
              </button>
            )}
            <button
              onClick={onClose}
              className={`transition ${data?.group_color ? 'text-gray-500 hover:text-gray-900' : 'text-white/70 hover:text-white'}`}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="flex border-b border-gray-100 flex-shrink-0 bg-gray-50">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide transition-all
                ${activeTab === tab ? 'border-b-2 border-sky-500 text-sky-600 bg-white' : 'text-gray-500 hover:text-gray-700'}`}>
              {tab}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-12"><CircularProgress size={28} /></div>
          ) : !data ? (
            <p className="text-gray-400 text-sm">Not found.</p>
          ) : (
            <>
              {activeTab === 'details' && (
                <div className="space-y-3">
                  {/* Pipeline status */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pipeline Status</label>
                    <select value={data.column_id} onChange={(e) => handleColumnChange(e.target.value)} disabled={saving}
                      className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300">
                      {boardColumns.map(col => <option key={col.id} value={col.id}>{col.label}</option>)}
                    </select>
                  </div>

                  {/* 2-col fields */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <EditableField label="Pharmacy Name" value={data.pharmacy_name || ''} required onSave={(v) => handleFieldSave('pharmacy_name', v)} />
                    </div>
                    <EditableField label="Phone" value={data.pharmacy_phone || ''} onSave={(v) => handleFieldSave('pharmacy_phone', v)} />
                    <EditableField label="Contact Name" value={data.contact_name || ''} onSave={(v) => handleFieldSave('contact_name', v)} />
                    <div className="col-span-2">
                      <EditableField label="Email" value={data.pharmacy_email || ''} type="email" onSave={(v) => handleFieldSave('pharmacy_email', v)} />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Primary App Focus</label>
                      <select value={data.primary_app_focus || ''} onChange={(e) => handleFieldSave('primary_app_focus', e.target.value)}
                        className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300">
                        {ALL_APPS.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Training email */}
                  <div className="flex items-center justify-between p-2.5 bg-sky-50 rounded-lg border border-sky-100">
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Training Booking Email</p>
                      <p className="text-xs text-gray-500">{data.training_email_sent ? 'Email sent ✓' : 'Not yet sent'}</p>
                    </div>
                    <button onClick={handleSendEmail} disabled={emailSending}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-sky-500 text-white text-xs font-semibold rounded-lg hover:bg-sky-600 disabled:opacity-50 transition">
                      <EmailOutlinedIcon style={{ fontSize: 13 }} />
                      {emailSending ? 'Sending…' : 'Send Email'}
                    </button>
                  </div>

                  {/* Chemist link */}
                  {showChemistLink && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                        <LinkIcon style={{ fontSize: 13 }} /> Link to Chemist Record
                      </label>
                      {data.chemist_id ? (
                        <p className="mt-0.5 text-sm text-emerald-600 font-medium">
                          Linked: {data.linked_chemist_name || `Chemist #${data.chemist_id}`}
                        </p>
                      ) : (
                        <select onChange={(e) => e.target.value && handleLinkChemist(e.target.value)} defaultValue=""
                          className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300">
                          <option value="">Select chemist to link…</option>
                          {chemists.map(c => <option key={c.chemistID} value={c.chemistID}>{c.chemistName}</option>)}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Not proceeding + Archive */}
                  <div className="pt-2 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={handleDeactivate}
                      className="flex items-center gap-2 px-3 py-2 text-xs text-red-500 font-semibold hover:bg-red-50 rounded-lg transition"
                    >
                      <BlockIcon style={{ fontSize: 14 }} /> Mark as Not Proceeding
                    </button>
                    {liveColId && data?.column_id === liveColId && onArchive && (
                      <button
                        onClick={() => { onArchive(cardId); onClose(); }}
                        className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500 font-semibold hover:bg-gray-100 rounded-lg transition"
                      >
                        <ArchiveOutlinedIcon style={{ fontSize: 14 }} /> Archive Lead
                      </button>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'apps' && (
                <div className="space-y-2">
                  {/* Header row with sync button */}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">Track interest, installation, and training per app.</p>
                    {data.chemist_id ? (
                      <div className="flex items-center gap-2">
                        {syncMsg && (
                          <span className={`text-xs font-semibold ${syncMsg === 'Synced' ? 'text-emerald-600' : 'text-red-500'}`}>
                            {syncMsg === 'Synced' ? '✓ Synced' : syncMsg}
                          </span>
                        )}
                        <button
                          onClick={handleSyncApps}
                          disabled={syncing}
                          title="Refresh installed status from tblChemists (Quda, MediDrop, Webbit, HaloPay, Zephyr)"
                          className="flex items-center gap-1 px-2 py-1 text-xs text-sky-600 font-semibold border border-sky-200 rounded-lg hover:bg-sky-50 disabled:opacity-40 transition"
                        >
                          <span className={syncing ? 'animate-spin inline-flex' : 'inline-flex'}>
                            <SyncIcon style={{ fontSize: 13 }} />
                          </span>
                          {syncing ? 'Syncing…' : 'Sync'}
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-400 italic">Link a chemist to enable sync</span>
                    )}
                  </div>

                  {/* Note on which apps are auto-synced */}
                  <p className="text-[10px] text-gray-400">
                    <span className="font-semibold text-gray-500">Auto-synced:</span> Quda · MediDrop · Webbit · HaloPay · Zephyr &nbsp;|&nbsp; DocLink &amp; HaloText are manual only.
                  </p>

                  <div className="rounded-xl border border-gray-100 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                          <th className="text-left px-4 py-2">App</th>
                          <th className="text-center px-2 py-2">Interested</th>
                          <th className="text-center px-2 py-2">Installed</th>
                          <th className="text-center px-2 py-2">Trained</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.apps || []).map((app) => (
                          <tr key={app.app_name} className="border-t border-gray-50">
                            <td className="px-4 py-2.5"><AppBadge app={app.app_name} size="sm" /></td>
                            {['is_interested','is_installed','is_trained'].map(field => (
                              <td key={field} className="text-center px-2 py-2">
                                <input
                                  type="checkbox"
                                  checked={!!app[field]}
                                  onChange={(e) => handleAppToggle(app.app_name, field, e.target.checked)}
                                  className="w-4 h-4 rounded accent-sky-500 cursor-pointer"
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'calls' && (
                <CardCallsTab context="lead" cardId={cardId} />
              )}

              {activeTab === 'comments' && (
                <CommentThread comments={data.comments || []} onAdd={handleAddComment} />
              )}

              {activeTab === 'history' && (
                <StatusHistoryLog history={data.history || []} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ArchivedLeadsPanel = ({ boardId, onClose, onRestored }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getArchivedCards(boardId);
      setCards(result?.cards || result || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [boardId]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (id) => {
    setRestoring(id);
    try {
      await unarchiveCard(id);
      setCards(prev => prev.filter(c => c.id !== id));
      onRestored && onRestored();
    } catch (e) { console.error(e); }
    setRestoring(null);
  };

  const filtered = search.trim()
    ? cards.filter(c => (c.pharmacy_name || '').toLowerCase().includes(search.toLowerCase()))
    : cards;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <ArchiveOutlinedIcon className="text-gray-500" style={{ fontSize: 20 }} />
            <h2 className="text-lg font-bold text-[#002169]">Archived Leads</h2>
            <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{cards.length}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition"><CloseIcon /></button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: 16 }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search archived leads…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8"><CircularProgress size={24} /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-8">
              {search ? 'No results match your search.' : 'No archived leads.'}
            </p>
          ) : (
            filtered.map(card => (
              <div key={card.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-800 truncate">{card.pharmacy_name}</p>
                    <AppBadge app={card.primary_app_focus} size="xs" />
                  </div>
                  {card.contact_name && <p className="text-xs text-gray-400 mt-0.5">{card.contact_name}</p>}
                  {card.column_label && <p className="text-xs text-gray-400">Was in: {card.column_label}</p>}
                </div>
                <button
                  onClick={() => handleRestore(card.id)}
                  disabled={restoring === card.id}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-50 transition flex-shrink-0"
                >
                  <UnarchiveOutlinedIcon style={{ fontSize: 14 }} />
                  {restoring === card.id ? 'Restoring…' : 'Restore'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const OnboardingBoard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNew, setShowNew] = useState(false);
  const [boardColumns, setBoardColumns] = useState([]);
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [activeId, setActiveId] = useState(null);

  // Archive panel
  const [showArchived, setShowArchived] = useState(false);

  // Multi-select bulk move
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTargetCol, setBulkTargetCol] = useState('');
  const [bulkMoving, setBulkMoving] = useState(false);

  const toggleSelect = (id) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const clearSelection = () => { setSelectedIds(new Set()); setBulkTargetCol(''); };

  const handleBulkMove = async () => {
    if (!bulkTargetCol || selectedIds.size === 0) return;
    setBulkMoving(true);
    try {
      await Promise.all([...selectedIds].map(id => moveCard(id, Number(bulkTargetCol))));
      clearSelection();
      loadBoard();
    } catch (e) { console.error(e); }
    setBulkMoving(false);
  };

  // Deep-link: open a specific card drawer when ?cardId=X is in the URL
  useEffect(() => {
    const id = searchParams.get('cardId');
    if (id) {
      setSelectedCardId(Number(id));
      // Clean the param so closing the drawer doesn't reopen it on back-nav
      setSearchParams(prev => { prev.delete('cardId'); return prev; }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const [board, result] = await Promise.all([
        getBoard(BOARD_ID),
        getCards({ board_id: BOARD_ID, limit: 500 }),
      ]);
      const cols = board?.columns || [];
      setBoardColumns(cols);

      const cards = result?.cards || [];
      const grouped = {};
      cols.forEach(c => { grouped[String(c.id)] = []; });
      cards.forEach(card => {
        const key = String(card.column_id);
        if (grouped[key]) grouped[key].push(card);
      });
      setColumns(grouped);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  // Derived live column ID — the archive trigger point
  const liveColId = useMemo(
    () => boardColumns.find(c => c.key === LIVE_KEY)?.id ?? null,
    [boardColumns]
  );

  const handleArchive = useCallback(async (cardId) => {
    // Optimistically remove from board
    setColumns(prev => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        next[key] = next[key].filter(c => c.id !== cardId);
      }
      return next;
    });
    try {
      await archiveCard(cardId);
    } catch (e) {
      console.error(e);
      loadBoard(); // roll back on failure
    }
  }, [loadBoard]);

  const COLUMN_IDS = new Set(boardColumns.map(c => String(c.id)));

  // Derived from loaded columns — no hardcoded IDs needed
  const NOT_PROCEEDING_COL = boardColumns.find(c => c.key === 'not_proceeding')?.id ?? null;
  const CHEMIST_LINK_COLS  = new Set(
    boardColumns.filter(c => !DEAD_END_COL_KEYS.has(c.key)).map(c => c.id)
  );

  const kanbanCollision = (args) => {
    const colHits = rectIntersection({
      ...args,
      droppableContainers: args.droppableContainers.filter(c => COLUMN_IDS.has(String(c.id))),
    });
    if (colHits.length > 0) return colHits;
    return closestCenter(args);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over) return;

    const activeStr = String(active.id);
    const overStr   = String(over.id);

    let sourceCol = null; let card = null;
    for (const [col, cards] of Object.entries(columns)) {
      const found = cards.find(c => String(c.id) === activeStr);
      if (found) { sourceCol = col; card = found; break; }
    }
    if (!sourceCol || !card) return;

    let targetCol = COLUMN_IDS.has(overStr) ? overStr : null;
    if (!targetCol) {
      for (const [col, cards] of Object.entries(columns)) {
        if (cards.find(c => String(c.id) === overStr)) { targetCol = col; break; }
      }
    }
    if (!targetCol || targetCol === sourceCol) return;

    setColumns(prev => {
      const next = { ...prev };
      next[sourceCol] = next[sourceCol].filter(c => String(c.id) !== activeStr);
      next[targetCol] = [{ ...card, column_id: Number(targetCol) }, ...next[targetCol]];
      return next;
    });

    try {
      await moveCard(card.id, Number(targetCol));
    } catch {
      loadBoard();
    }
  };

  // Clear selection whenever the board reloads (drag, bulk move, etc.)
  // (loadBoard sets loading=true first, so we piggyback on that transition)

  const filteredColumns = search.trim()
    ? Object.fromEntries(
        Object.entries(columns).map(([k, cards]) => [
          k,
          cards.filter(c => (c.pharmacy_name || '').toLowerCase().includes(search.toLowerCase())),
        ])
      )
    : columns;

  const draggedCard = activeId
    ? Object.values(columns).flat().find(c => String(c.id) === String(activeId))
    : null;

  const firstColumnId = boardColumns[0]?.id || 1;

  if (loading) return (
    <div className="flex justify-center items-center h-full">
      <CircularProgress style={{ color: '#0ea5e9' }} />
    </div>
  );

  // All visible card ids (respects current search filter)
  const allVisibleIds = Object.values(filteredColumns).flat().map(c => c.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedIds.has(id));

  return (
    <div className="flex flex-col gap-3">
      {/* ── Toolbar row — search + New Lead always visible; bulk controls appear to the right ── */}
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: 18 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pharmacies…"
            className="w-56 pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-300 bg-white"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
            >
              <CloseIcon style={{ fontSize: 16 }} />
            </button>
          )}
        </div>

        {/* New Lead */}
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#002169] text-white text-sm font-semibold rounded-lg hover:bg-[#001550] transition shadow-sm flex-shrink-0"
        >
          <AddIcon style={{ fontSize: 18 }} /> New Lead
        </button>

        {/* Archived leads */}
        <button
          onClick={() => setShowArchived(true)}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-sm font-semibold rounded-lg hover:bg-gray-50 transition flex-shrink-0"
        >
          <ArchiveOutlinedIcon style={{ fontSize: 16 }} /> Archived
        </button>

        {/* Bulk-move controls — slide in after New Lead when cards are selected */}
        {selectedIds.size > 0 && (
          <>
            <div className="w-px h-6 bg-gray-200 flex-shrink-0" />
            <button
              onClick={clearSelection}
              className="text-gray-400 hover:text-gray-600 transition flex-shrink-0"
              title="Clear selection"
            >
              <CloseIcon style={{ fontSize: 16 }} />
            </button>
            <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
              {selectedIds.size} selected
            </span>
            <button
              onClick={() => allSelected ? clearSelection() : setSelectedIds(new Set(allVisibleIds))}
              className="text-xs text-sky-600 hover:text-sky-800 underline underline-offset-2 transition whitespace-nowrap"
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
            <span className="text-sm text-gray-500 whitespace-nowrap flex-shrink-0">Move to:</span>
            <select
              value={bulkTargetCol}
              onChange={(e) => setBulkTargetCol(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300 bg-white min-w-[150px]"
            >
              <option value="">Select column…</option>
              {boardColumns.map(col => (
                <option key={col.id} value={col.id}>{col.label}</option>
              ))}
            </select>
            <button
              onClick={handleBulkMove}
              disabled={!bulkTargetCol || bulkMoving}
              className="px-4 py-1.5 bg-sky-500 text-white text-sm font-bold rounded-lg hover:bg-sky-600 disabled:opacity-40 transition whitespace-nowrap flex-shrink-0"
            >
              {bulkMoving ? 'Moving…' : 'Move'}
            </button>
          </>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={kanbanCollision}
        onDragStart={({ active }) => setActiveId(active.id)}
        onDragEnd={handleDragEnd}
      >
        <div
          className="flex gap-3 p-3 overflow-x-auto border border-gray-200 rounded-xl bg-gray-50 min-w-0"
          style={{ height: 'calc(100vh - 220px)' }}
        >
          {boardColumns.map(col => (
            <KanbanColumn
              key={col.id}
              id={String(col.id)}
              label={col.label}
              cards={filteredColumns[String(col.id)] || []}
              accentColor={col.color}
              headerBg={col.header_color}
            >
              {(filteredColumns[String(col.id)] || []).map(card => (
                <LeadCard
                  key={card.id}
                  card={card}
                  onClick={() => setSelectedCardId(card.id)}
                  onChemistClick={(uuid) => navigate(`/admin-functions/chemist/${uuid}`)}
                  isSelected={selectedIds.has(card.id)}
                  onToggleSelect={toggleSelect}
                  onArchive={col.id === liveColId ? handleArchive : undefined}
                />
              ))}
            </KanbanColumn>
          ))}
        </div>

        <DragOverlay>
          {draggedCard && (
            <div className="bg-white rounded-lg shadow-lg border border-sky-200 p-3 w-56 rotate-2 scale-105">
              <p className="text-sm font-semibold text-gray-800">{draggedCard.pharmacy_name}</p>
              <AppBadge app={draggedCard.primary_app_focus} size="xs" />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {showNew && (
        <NewLeadModal
          boardColumns={boardColumns}
          onClose={() => setShowNew(false)}
          onCreated={loadBoard}
        />
      )}

      {selectedCardId && (
        <LeadDetailDrawer
          cardId={selectedCardId}
          boardColumns={boardColumns}
          liveColId={liveColId}
          onArchive={handleArchive}
          onClose={() => setSelectedCardId(null)}
          onUpdate={loadBoard}
        />
      )}

      {showArchived && (
        <ArchivedLeadsPanel
          boardId={BOARD_ID}
          onClose={() => setShowArchived(false)}
          onRestored={loadBoard}
        />
      )}
    </div>
  );
};

export default OnboardingBoard;